import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Loader2 } from "lucide-react";
import StaffHeader from "@/components/StaffHeader";
import CaseCard from "@/components/CaseCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const TABS = [
  { label: "All", value: "All" },
  { label: "New", value: "New" },
  { label: "Under Care", value: "Under Care" },
  { label: "Released", value: "Released" },
  { label: "Adopted", value: "Adopted" },
  { label: "Sanctuary", value: "Sanctuary Resident" },
  { label: "Passed Away", value: "Passed Away" },
];
const PAGE_SIZE = 20;

export default function Dashboard() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [tab, setTab] = useState("Under Care");
  const [search, setSearch] = useState("");
  const sentinelRef = useRef(null);

  const fetchCases = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: 0, limit: PAGE_SIZE };
      if (tab !== "All") params.status = tab;
      if (search) params.search = search;
      const { data } = await api.get("/cases", { params });
      setCases(data.items);
      setHasMore(data.has_more);
    } finally {
      setLoading(false);
    }
  }, [tab, search]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const params = { skip: cases.length, limit: PAGE_SIZE };
      if (tab !== "All") params.status = tab;
      if (search) params.search = search;
      const { data } = await api.get("/cases", { params });
      setCases((prev) => [...prev, ...data.items]);
      setHasMore(data.has_more);
    } finally {
      setLoadingMore(false);
    }
  }, [cases.length, tab, search, hasMore, loadingMore]);

  useEffect(() => {
    const t = setTimeout(fetchCases, 250);
    return () => clearTimeout(t);
  }, [fetchCases]);

  useEffect(() => {
    if (!sentinelRef.current || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "200px" }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [loadMore, hasMore]);

  return (
    <div className="min-h-screen bg-amtm-bg">
      <StaffHeader />
      <div className="max-w-3xl mx-auto px-4 py-6 pb-24">
        <div className="grid grid-cols-2 gap-3 mb-6">
          <Link to="/cases/new" data-testid="new-case-button" className="block">
            <Button className="w-full h-14 text-base font-semibold bg-amtm-teal text-white hover:bg-amtm-tealHover rounded-xl shadow-md">
              <Plus className="h-5 w-5 mr-1.5" /> New Case
            </Button>
          </Link>
          <Link to="/sanctuary-residents/new" data-testid="new-sanctuary-resident-button" className="block">
            <Button className="w-full h-14 text-base font-semibold bg-amtm-teal text-white hover:bg-amtm-teal/90 rounded-xl shadow-md">
              <Plus className="h-5 w-5 mr-1.5" /> Sanctuary Resident
            </Button>
          </Link>
        </div>

        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-amtm-textMuted" />
          <Input
            placeholder="Search by name or case #..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="search-input"
            className="pl-9 h-11"
          />
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2 mb-4 -mx-1 px-1">
          {TABS.map((t) => (
            <button
              key={t.value}
              onClick={() => setTab(t.value)}
              data-testid={`filter-tab-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                tab === t.value ? "bg-amtm-teal text-amtm-text" : "bg-amtm-surface border border-amtm-border text-amtm-textMuted"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16" data-testid="dashboard-loading">
            <Loader2 className="h-8 w-8 animate-spin text-amtm-teal" />
          </div>
        ) : cases.length === 0 ? (
          <p className="text-center text-amtm-textMuted py-16" data-testid="dashboard-empty-state">
            No cases found for "{tab}".
          </p>
        ) : (
          <>
            <div className="space-y-3" data-testid="case-list">
              {cases.map((c) => (
                <CaseCard key={c.id} caseItem={c} />
              ))}
            </div>
            {hasMore && (
              <div ref={sentinelRef} className="flex justify-center py-6" data-testid="dashboard-load-more-sentinel">
                {loadingMore && <Loader2 className="h-6 w-6 animate-spin text-amtm-teal" data-testid="dashboard-loading-more" />}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
